import React from 'react'
import { UI_TEXT_CLEAR_SEARCH } from '../../constants/uiText'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  SwipeableDrawer,
  TextField,
  Typography
} from '@mui/material'
import GoogleIcon from '../../icon/GoogleIcon'
import {
  UI_COLOR_BORDER_BUTTON,
  UI_COLOR_BORDER_BUTTON_HOVER,
  UI_COLOR_BORDER_LIGHT,
  UI_COLOR_BORDER_SUBTLE,
  UI_COLOR_PRIMARY,
  UI_COLOR_PRIMARY_BADGE_SHADOW,
  UI_COLOR_PRIMARY_HOVER,
  UI_COLOR_SURFACE_HOVER,
  UI_COLOR_TEXT_MUTED,
  UI_COLOR_TEXT_STRONG,
  UI_COLOR_WHITE
} from '../../constants/uiColors'
import {
  MOBILE_ADVANCED_FILTER_DRAG_HANDLE_SX,
  MOBILE_ADVANCED_FILTER_SEARCH_SX
} from './styles/mobileAdvancedFilterSx'

export default function MobileAdvancedFilterDrawer({
  title,
  textSizeOffset = 0,
  drawerTitle,
  query,
  onQueryChange,
  onQueryKeyDown,
  onClearQuery,
  searchInputRef,
  searchPlaceholder = 'Buscar...',
  isOpen,
  onOpen,
  onClose,
  appliedFilterGroupCount = 0,
  appliedFilterChips = [],
  appliedChipsContent = null,
  filterFieldsContent,
  onClearAll,
  onApply,
  applyButtonText = 'Aplicar filtros',
  clearButtonText = 'Limpiar todo',
  openFiltersAriaLabel = 'Abrir filtros',
  closeFiltersAriaLabel = 'Cerrar filtros'
}) {
  const normalizedQuery = String(query || '')

  return (
    <>
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          '--report-text-size-offset': `${textSizeOffset}px`,
          p: 1.5,
          mb: 2,
          borderRadius: 1,
          borderColor: UI_COLOR_BORDER_SUBTLE,
          bgcolor: UI_COLOR_WHITE
        }}
      >
        <Stack spacing={1.15}>
          <Typography
            sx={{
              fontSize: 'calc(11px + var(--report-text-size-offset, 0px))',
              fontWeight: 500,
              color: UI_COLOR_TEXT_MUTED,
              lineHeight: 1.2,
              textTransform: 'uppercase',
              letterSpacing: '0.04em'
            }}
          >
            {title}
          </Typography>

          <Stack direction="row" spacing={1.25} alignItems="stretch">
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder={searchPlaceholder}
              value={normalizedQuery}
              onChange={onQueryChange}
              onKeyDown={onQueryKeyDown}
              inputRef={searchInputRef}
              inputProps={{
                enterKeyHint: 'search'
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {normalizedQuery ? (
                      <IconButton
                        size="small"
                        aria-label={UI_TEXT_CLEAR_SEARCH}
                        onClick={onClearQuery}
                        edge="end"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <SearchIcon fontSize="small" color="action" />
                    )}
                  </InputAdornment>
                )
              }}
              sx={MOBILE_ADVANCED_FILTER_SEARCH_SX}
            />

            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <IconButton
                aria-label={openFiltersAriaLabel}
                onClick={onOpen}
                sx={{
                  width: 37,
                  height: 37,
                  borderRadius: '4px',
                  border: '1px solid transparent',
                  bgcolor: UI_COLOR_WHITE,
                  color: UI_COLOR_PRIMARY,
                  '&:hover': {
                    bgcolor: UI_COLOR_SURFACE_HOVER
                  }
                }}
              >
                <GoogleIcon
                  className="filter-icon-poc oc-icon-primary"
                  name="page_info"
                  size={22}
                  style={{
                    marginRight: 0,
                    marginTop: 0
                  }}
                />
              </IconButton>

              {appliedFilterGroupCount > 0 ? (
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    top: -4,
                    right: -4,
                    minWidth: 20,
                    height: 20,
                    px: 0.5,
                    borderRadius: 999,
                    bgcolor: UI_COLOR_PRIMARY,
                    color: UI_COLOR_WHITE,
                    fontSize: 'calc(11px + var(--report-text-size-offset, 0px))',
                    fontWeight: 800,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: UI_COLOR_PRIMARY_BADGE_SHADOW
                  }}
                >
                  {appliedFilterGroupCount}
                </Box>
              ) : null}
            </Box>
          </Stack>

          {appliedChipsContent ||
          (Array.isArray(appliedFilterChips) && appliedFilterChips.length > 0)
            ? appliedChipsContent || (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    overflowX: 'auto',
                    pb: 0.5,
                    pr: 0.25,
                    '&::-webkit-scrollbar': { display: 'none' }
                  }}
                >
                  {appliedFilterChips.map((chip) => (
                    <Chip
                      key={chip.key}
                      label={chip.label}
                      size="small"
                      variant="outlined"
                      sx={{
                        flexShrink: 0,
                        maxWidth: 260,
                        borderRadius: 999,
                        borderColor: '#FF893D',
                        bgcolor: '#FF893D',
                        color: UI_COLOR_WHITE,
                        '& .MuiChip-label': {
                          px: 1.25,
                          color: UI_COLOR_WHITE,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }
                      }}
                    />
                  ))}
                </Stack>
              )
            : null}
        </Stack>
      </Paper>

      <SwipeableDrawer
        anchor="bottom"
        open={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        disableSwipeToOpen
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            '--report-text-size-offset': `${textSizeOffset}px`,
            width: { xs: '100%', sm: 'min(720px, calc(100% - 32px))' },
            left: 0,
            right: 0,
            mx: 'auto',
            height: 'auto',
            maxHeight: { xs: '86vh', sm: 'min(760px, 86vh)' },
            borderRadius: '28px 28px 0 0',
            boxShadow: '0 -18px 44px rgba(15, 23, 42, 0.18)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: UI_COLOR_WHITE
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: '0 1 auto',
            minHeight: 0
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              pt: 1.25,
              pb: 1.75,
              bgcolor: UI_COLOR_WHITE,
              borderBottom: `1px solid ${UI_COLOR_BORDER_LIGHT}`,
              flexShrink: 0
            }}
          >
            <Box sx={MOBILE_ADVANCED_FILTER_DRAG_HANDLE_SX} />

            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 'calc(18px + var(--report-text-size-offset, 0px))',
                    fontWeight: 800,
                    color: UI_COLOR_TEXT_STRONG
                  }}
                >
                  {drawerTitle || title}
                </Typography>
              </Box>

              <IconButton
                aria-label={closeFiltersAriaLabel}
                onClick={onClose}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 0,
                  border: 'none',
                  bgcolor: 'transparent',
                  color: UI_COLOR_TEXT_MUTED,
                  '&:hover': {
                    bgcolor: 'transparent'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: '0 1 auto',
              minHeight: 0,
              maxHeight: { xs: 'calc(86vh - 138px)', sm: 'calc(86vh - 138px)' },
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              px: { xs: 2, sm: 3 },
              pt: { xs: 1.5, sm: 2 },
              pb: { xs: 2, sm: 3 },
              bgcolor: UI_COLOR_WHITE
            }}
          >
            {filterFieldsContent}
          </Box>

          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              pt: 1.25,
              pb: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
              bgcolor: UI_COLOR_WHITE,
              borderTop: `1px solid ${UI_COLOR_BORDER_LIGHT}`,
              flexShrink: 0
            }}
          >
            <Stack direction="row" spacing={1.25}>
              <Button
                variant="outlined"
                onClick={onClearAll}
                fullWidth
                sx={{
                  minHeight: 48,
                  borderRadius: 3,
                  borderColor: UI_COLOR_BORDER_BUTTON,
                  color: UI_COLOR_PRIMARY,
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: UI_COLOR_BORDER_BUTTON_HOVER,
                    bgcolor: UI_COLOR_WHITE
                  }
                }}
              >
                {clearButtonText}
              </Button>
              <Button
                variant="contained"
                disableElevation
                onClick={onApply}
                fullWidth
                sx={{
                  minHeight: 48,
                  borderRadius: 3,
                  bgcolor: UI_COLOR_PRIMARY,
                  color: UI_COLOR_WHITE,
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: UI_COLOR_PRIMARY_HOVER
                  }
                }}
              >
                {applyButtonText}
              </Button>
            </Stack>
          </Box>
        </Box>
      </SwipeableDrawer>
    </>
  )
}
